import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';
import {
    EzString,
    EzArray
} from '/ezlibrary/helpers/EzHelpers.js';
import { EzClockerViewId } from '/ezlibrary/enums/EzClockerViewId.js';

/**
 * @class
 * @extends {EzEnumeration2}
 * @description
 * Defines the known feature toggle ids
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * ENGINEERING NOTES
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Keep this file in sync with the EzFeatureToggleId.java enumeration.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-enumeration classes with:
 *     import {
 *         // < other enumeration classes ... >
 *         EzFeatureToggleId
 *     } from '/ezlibrary/enums/EzEnumerations.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into enumeration classes with:
 *     import { EzFeatureToggleId } from '/ezlibrary/enums/EzFeatureToggleId.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Access static method/properties only:
 *     EzFeatureToggleId.{property or method name}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzFeatureToggleId extends EzEnumeration2 {
    /**
     * @static
     * @public @method
     * Example static method that returns the enumeration property's data.display value.
     * If the enumPropertyValue is not a valid enumeration property value, then EzEnumTemplate.UNKNOWN.display is returned.
     * @param {undefined|null|string} enumPropertyValue
     * A valid enumeration property value from EzEnumTemplate
     * @returns {string}
     */
    static ezToDisplayName(enumPropertyValue) {
        return EzString.stringOrEmpty(
            EzString.hasLength(enumPropertyValue)
                ? EzEnumTemplate.ezEnumData(enumPropertyValue)?.display
                : EzEnumTemplate.ezEnumData(EzEnumTemplate.UNKNOWN)?.display);
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Initialization Section: ONLY PLACE INITIALIZATION CODE BEYOND THIS SECTION
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     * @type {EzEnumTemplate}
     * Internal static instance of the EzEnumTemplate
     */
    static #ezFeatureToggleIdSingleton = null;

    /**
     * @static
     * @private @method
     * Builds the enumeration properties and values for EzClockerAccountType
     */
    static #ezBuildEnumeration() {
        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Unknown Features
         * Feature Group: ezfgUnknownFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'UNKNOWN',
            // Enumeration Property Value
            'UNKNOWN',
            // Optional Enumeration Data Value
            {
                value: 'UNKNOWN',
                display: 'UNKNOWN',
                featureGroupId: 'ezfgUnknownFeatures',
                usedInViewIds: EzArray.EMPTY
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Team Chat Features
         * Feature Group: ezfgTeamChatFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfTeamChat',
            'ezfTeamChat',
            {
                value: 'ezfTeamChat',
                display: 'ezfTeamChat',
                featureGroupId: 'ezfgTeamChatFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW,

                    EzClockerViewId.ezEmployerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboard,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.EMPLOYER_DASHBOARD_VIEW,

                    EzClockerViewId.ezEmployerScheduleView,
                    // Alias for EzClockerViewId.ezEmployerScheduleView
                    EzClockerViewId.employerSchedule,
                    // Alias for EzClockerViewId.ezEmployerScheduleView
                    EzClockerViewId.EMPLOYER_SCHEDULE_VIEW,

                    EzClockerViewId.ezEmployeeDashboardView,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.ezEmployeeDashboard,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.employeeDashboard,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.EMPLOYEE_DASHBOARD_VIEW,

                    EzClockerViewId.ezEmployeeScheduleView,
                    // Alias for EzClockerViewId.ezEmployeeScheduleView
                    EzClockerViewId.employeeSchedule,
                    // Alias for EzClockerViewId.ezEmployeeScheduleView
                    EzClockerViewId.EMPLOYEE_SCHEDULE_VIEW,

                    EzClockerViewId.ezTeamChatDialog,
                    // Alias for EzClockerViewId.ezTeamChatDialog
                    EzClockerViewId.teamChatView,
                    // Alias for EzClockerViewId.ezTeamChatDialog
                    EzClockerViewId.TEAM_CHAT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezTeamChatDialog',
            'ezTeamChatDialog',
            {
                value: 'ezTeamChatDialog',
                display: 'ezTeamChatDialog',
                featureGroupId: 'ezfgTeamChatFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezTeamChatDialog,
                    // Alias for EzClockerViewId.ezTeamChatDialog
                    EzClockerViewId.teamChatView,
                    // Alias for EzClockerViewId.ezTeamChatDialog
                    EzClockerViewId.TEAM_CHAT_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Feature Group: ezfgIntegrationFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfIntegrations',
            'ezfIntegrations',
            {
                value: 'ezfIntegrations',
                display: 'ezfIntegrations',
                featureGroupId: 'ezfgIntegrationFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW,

                    EzClockerViewId.ezEmployerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboard,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.EMPLOYER_DASHBOARD_VIEW,
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Feature Group: ezfgUnpaidBreakFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_ALLOW_RECORDING_OF_UNPAID_BREAKS',
            'ezfEZOPTION_ALLOW_RECORDING_OF_UNPAID_BREAKS',
            {
                value: 'ezfEZOPTION_ALLOW_RECORDING_OF_UNPAID_BREAKS',
                display: 'ezfEZOPTION_ALLOW_RECORDING_OF_UNPAID_BREAKS',
                featureGroupId: 'ezfgUnpaidBreakFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Feature Group: ezfgAutomaticBreakFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_ALLOW_AUTOMATIC_BREAKS',
            'ezfEZOPTION_ALLOW_AUTOMATIC_BREAKS',
            {
                value: 'ezfEZOPTION_ALLOW_AUTOMATIC_BREAKS',
                display: 'ezfEZOPTION_ALLOW_AUTOMATIC_BREAKS',
                featureGroupId: 'ezfgAutomaticBreakFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Feature Group: ezfgUnpaidBreakFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_NOTIFY_EMPLOYEE_MEAL_BREAK_AFTER_HOURS',
            'ezfEZOPTION_NOTIFY_EMPLOYEE_MEAL_BREAK_AFTER_HOURS',
            {
                value: 'ezfEZOPTION_NOTIFY_EMPLOYEE_MEAL_BREAK_AFTER_HOURS',
                display: 'ezfEZOPTION_NOTIFY_EMPLOYEE_MEAL_BREAK_AFTER_HOURS',
                featureGroupId: 'ezfgUnpaidBreakFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });


        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Time Off Features
         * Feature Group: ezfgTimeOffFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
       this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezRestrictTimeOffRequests',
            'ezRestrictTimeOffRequests',
            {
                value: 'ezRestrictTimeOffRequests',
                display: 'ezRestrictTimeOffRequests',
                featureGroupId: 'ezfgTimeOffFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfTimeOff',
            'ezfTimeOff',
            {
                value: 'ezfTimeOff',
                display: 'ezfTimeOff',
                featureGroupId: 'ezfgTimeOffFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW,

                    EzClockerViewId.ezEmployerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboard,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.EMPLOYER_DASHBOARD_VIEW,

                    EzClockerViewId.ezEmployerScheduleView,
                    // Alias for EzClockerViewId.ezEmployerScheduleView
                    EzClockerViewId.employerSchedule,
                    // Alias for EzClockerViewId.ezEmployerScheduleView
                    EzClockerViewId.EMPLOYER_SCHEDULE_VIEW,

                    EzClockerViewId.ezEmployeeDashboardView,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.ezEmployeeDashboard,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.employeeDashboard,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.EMPLOYEE_DASHBOARD_VIEW,

                    EzClockerViewId.ezEmployeeScheduleView,
                    // Alias for EzClockerViewId.ezEmployeeScheduleView
                    EzClockerViewId.employeeSchedule,
                    // Alias for EzClockerViewId.ezEmployeeScheduleView
                    EzClockerViewId.EMPLOYEE_SCHEDULE_VIEW,

                    EzClockerViewId.ezEmployerTimeOffView,
                    // Alias for EzClockerViewId.ezEmployerTimeOffView
                    EzClockerViewId.employerTimeOffView,
                    // Alias for EzClockerViewId.ezEmployerTimeOffView
                    EzClockerViewId.EMPLOYER_TIME_OFF_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_ALLOW_TIME_OFF_REQUESTS',
            'ezfEZOPTION_ALLOW_TIME_OFF_REQUESTS',
            {
                value: 'ezfEZOPTION_ALLOW_TIME_OFF_REQUESTS',
                display: 'ezfEZOPTION_ALLOW_TIME_OFF_REQUESTS',
                featureGroupId: 'ezfgTimeOffFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezEmployerTimeOffView',
            'ezEmployerTimeOffView',
            {
                value: 'ezEmployerTimeOffView',
                display: 'ezEmployerTimeOffView',
                featureGroupId: 'ezfgTimeOffFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerTimeOffView,
                    // Alias for EzClockerViewId.ezEmployerTimeOffView
                    EzClockerViewId.employerTimeOffView,
                    // Alias for EzClockerViewId.ezEmployerTimeOffView
                    EzClockerViewId.EMPLOYER_TIME_OFF_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Feature Group: ezfgTimeZoneFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfActiveTimeZoneDisplay',
            'ezfActiveTimeZoneDisplay',
            {
                value: 'ezfActiveTimeZoneDisplay',
                display: 'ezfActiveTimeZoneDisplay',
                featureGroupId: 'ezfgTimeZoneFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboard,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.EMPLOYER_DASHBOARD_VIEW,

                    EzClockerViewId.ezEmployerScheduleView,
                    // Alias for EzClockerViewId.ezEmployerScheduleView
                    EzClockerViewId.employerSchedule,
                    // Alias for EzClockerViewId.ezEmployerScheduleView
                    EzClockerViewId.EMPLOYER_SCHEDULE_VIEW,
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Feature Group: ezfgReportFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEmployePdfReport',
            'ezfEmployePdfReport',
            {
                value: 'ezfEmployePdfReport',
                display: 'ezfEmployePdfReport',
                featureGroupId: 'ezfgReportFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboard,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.EMPLOYER_DASHBOARD_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Feature Group: ezfgScheduleFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezScheduleDrivenClockIn',
            'ezScheduleDrivenClockIn',
            {
                value: 'ezScheduleDrivenClockIn',
                display: 'ezScheduleDrivenClockIn',
                featureGroupId: 'ezfgScheduleFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_ALLOW_EMPLOYEES_TO_SEE_COWORKER_SCHEDULES',
            'ezfEZOPTION_ALLOW_EMPLOYEES_TO_SEE_COWORKER_SCHEDULES',
            {
                value: 'ezfEZOPTION_ALLOW_EMPLOYEES_TO_SEE_COWORKER_SCHEDULES',
                display: 'ezfEZOPTION_ALLOW_EMPLOYEES_TO_SEE_COWORKER_SCHEDULES',
                featureGroupId: 'ezfgScheduleFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfPublishSchedule',
            'ezfPublishSchedule',
            {
                value: 'ezfPublishSchedule',
                display: 'ezfPublishSchedule',
                featureGroupId: 'ezfgScheduleFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerScheduleView,
                    // Alias for EzClockerViewId.ezEmployerScheduleView
                    EzClockerViewId.employerSchedule,
                    // Alias for EzClockerViewId.ezEmployerScheduleView
                    EzClockerViewId.EMPLOYER_SCHEDULE_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfExportSchedule',
            'ezfExportSchedule',
            {
                value: 'ezfExportSchedule',
                display: 'ezfExportSchedule',
                featureGroupId: 'ezfgScheduleFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerScheduleView,
                    // Alias for EzClockerViewId.ezEmployerScheduleView
                    EzClockerViewId.employerSchedule,
                    // Alias for EzClockerViewId.ezEmployerScheduleView
                    EzClockerViewId.EMPLOYER_SCHEDULE_VIEW,

                    'employeeSchedule'
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * EzClocker Long Running Reports Service Features
         * Feature Group: ezfgEzClockerLongRunningReportServiceFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEzClockerLongRunningReportsService',
            'ezfEzClockerLongRunningReportsService',
            {
                value: 'ezfEzClockerLongRunningReportsService',
                display: 'ezfEzClockerLongRunningReportsService',
                featureGroupId: 'ezfgEzClockerLongRunningReportServiceFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboard,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.EMPLOYER_DASHBOARD_VIEW,

                    EzClockerViewId.ezEmployeeDashboardView,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.ezEmployeeDashboard,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.employeeDashboard,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.EMPLOYEE_DASHBOARD_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Time Period Features
         * Feature Group: ezfgTimePeriodFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_CUSTOM_PERIOD_START_TIME',
            'ezfEZOPTION_CUSTOM_PERIOD_START_TIME',
            {
                value: 'ezfEZOPTION_CUSTOM_PERIOD_START_TIME',
                display: 'ezfEZOPTION_CUSTOM_PERIOD_START_TIME',
                featureGroupId: 'ezfgTimePeriodFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Time Entry Features
         * Feature Group: ezfgTimeEntryFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_REQUIRE_LOCATION_FOR_CLOCKINOUT',
            'ezfEZOPTION_REQUIRE_LOCATION_FOR_CLOCKINOUT',
            {
                value: 'ezfEZOPTION_REQUIRE_LOCATION_FOR_CLOCKINOUT',
                display: 'ezfEZOPTION_REQUIRE_LOCATION_FOR_CLOCKINOUT',
                featureGroupId: 'ezfgTimeEntryFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezRoundClockInClockOut',
            'ezRoundClockInClockOut',
            {
                value: 'ezRoundClockInClockOut',
                display: 'ezRoundClockInClockOut',
                featureGroupId: 'ezfgTimeEntryFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfShowTimeEntryImages',
            'ezfShowTimeEntryImages',
            {
                value: 'ezfShowTimeEntryImages',
                display: 'ezfShowTimeEntryImages',
                featureGroupId: 'ezfgTimeEntryFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboard,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.EMPLOYER_DASHBOARD_VIEW,

                    EzClockerViewId.ezEmployeeDashboardView,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.ezEmployeeDashboard,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.employeeDashboard,
                    // Alias for EzClockerViewId.ezEmployeeDashboardView
                    EzClockerViewId.EMPLOYEE_DASHBOARD_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_RESTRICT_CLOCK_IN_OUT_TO_RADIUS_WITHIN_JOB_LOCATION',
            'ezfEZOPTION_RESTRICT_CLOCK_IN_OUT_TO_RADIUS_WITHIN_JOB_LOCATION',
            {
                value: 'ezfEZOPTION_RESTRICT_CLOCK_IN_OUT_TO_RADIUS_WITHIN_JOB_LOCATION',
                display: 'ezfEZOPTION_RESTRICT_CLOCK_IN_OUT_TO_RADIUS_WITHIN_JOB_LOCATION',
                featureGroupId: 'ezfgTimeEntryFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Feature Group: ezfgJobFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'JOBS',
            'JOBS',
            {
                value: 'JOBS',
                display: 'JOBS',
                featureGroupId: 'ezfgJobFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboard,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.EMPLOYER_DASHBOARD_VIEW
                ]
            });


        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Feature Group: ezfgEzClockerNotificationServiceFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEzClockerNotificationService',
            'ezfEzClockerNotificationService',
            {
                value: 'ezfEzClockerNotificationService',
                display: 'ezfEzClockerNotificationService',
                featureGroupId: 'ezfgEzClockerNotificationServiceFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_SEND_SCHEDULE_NOTIFICATIONS',
            'ezfEZOPTION_SEND_SCHEDULE_NOTIFICATIONS',
            {
                value: 'ezfEZOPTION_SEND_SCHEDULE_NOTIFICATIONS',
                display: 'ezfEZOPTION_SEND_SCHEDULE_NOTIFICATIONS',
                featureGroupId: 'ezfgEzClockerNotificationServiceFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfRemindEmployeesBeforeShiftStarts',
            'ezfRemindEmployeesBeforeShiftStarts',
            {
                value: 'ezfRemindEmployeesBeforeShiftStarts',
                display: 'ezfRemindEmployeesBeforeShiftStarts',
                featureGroupId: 'ezfgEzClockerNotificationServiceFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_NOTIFY_EMPLOYER_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
            'ezfEZOPTION_NOTIFY_EMPLOYER_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
            {
                value: 'ezfEZOPTION_NOTIFY_EMPLOYER_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
                display: 'ezfEZOPTION_NOTIFY_EMPLOYER_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
                featureGroupId: 'ezfgEzClockerNotificationServiceFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_NOTIFY_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
            'ezfEZOPTION_NOTIFY_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
            {
                value: 'ezfEZOPTION_NOTIFY_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
                display: 'ezfEZOPTION_NOTIFY_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
                featureGroupId: 'ezfgEzClockerNotificationServiceFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfEZOPTION_NOTIFY_EMPLOYEE_BEFORE_SHIFT_STARTS',
            'ezfEZOPTION_NOTIFY_EMPLOYEE_BEFORE_SHIFT_STARTS',
            {
                value: 'ezfEZOPTION_NOTIFY_EMPLOYEE_BEFORE_SHIFT_STARTS',
                display: 'ezfEZOPTION_NOTIFY_EMPLOYEE_BEFORE_SHIFT_STARTS',
                featureGroupId: 'ezfgEzClockerNotificationServiceFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Subscription Features
         * Feature Group: ezfgSubscriptionFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezfYearlySubscriptions2023',
            'ezfYearlySubscriptions2023',
            {
                value: 'ezfYearlySubscriptions2023',
                display: 'ezfYearlySubscriptions2023',
                featureGroupId: 'ezfgSubscriptionFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerAccountDetailsView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.employerAccountView,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.account,
                    // Alias for EzClockerViewId.ezEmployerAccountDetailsView
                    EzClockerViewId.EMPLOYER_ACCOUNT_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Refer-A-Friend Features
         * Feature Group: ezfgReferAFriendFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezReferAFriend',
            'ezReferAFriend',
            {
                value: 'ezReferAFriend',
                display: 'ezReferAFriend',
                featureGroupId: 'ezfgReferAFriendFeatures',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboard,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.EMPLOYER_DASHBOARD_VIEW
                ]
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Refer-A-Friend Features
         * Feature Group: ezfgHelpAiChat
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezFeatureToggleIdSingleton.ezBuildEnumProp(
            'ezHelpAiChat',
            'ezHelpAiChat',
            {
                value: 'ezHelpAiChat',
                display: 'ezHelpAiChat',
                featureGroupId: 'ezfgHelpAiChat',
                usedInViewIds: [
                    EzClockerViewId.ezEmployerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboardView,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.employerDashboard,
                    // Alias for EzClockerViewId.ezEmployerDashboardView
                    EzClockerViewId.EMPLOYER_DASHBOARD_VIEW
                ]
            });
    }

    /**
     * Static Initialization of EzExportAction
     */
    static {
        if (null == this.#ezFeatureToggleIdSingleton) {
            this.#ezFeatureToggleIdSingleton = new EzFeatureToggleId();

            globalThis.window.EzFeatureToggleId = this.#ezFeatureToggleIdSingleton;

            this.#ezBuildEnumeration();
        }
    }

    /**
     * @public @constructor
     * >> DO NOT CREATE NEW INSTANCES <<
     * The static initializer will create a singleton instance that injects the static properties and methods
     * that you then use to access enumeration values.
     */
    constructor(name, value, optionalData) {
        super(name, value, optionalData);
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * End of Initialziation Section: !! DO NOT PLACE ANY CODE BEYOND THIS SECTION !!
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
}
